import chunk from 'lodash/chunk';
import memoize from 'lodash/memoize';

const MAX_VERTICES = 2000;
export const NO_DATA = -1000;

function fetchElevation(geometry: GeoJSON.Point, elevation?: number): Promise<GeoJSON.Point>;

function fetchElevation(
    geometry: GeoJSON.LineString,
    elevation?: number
): Promise<GeoJSON.LineString>;

function fetchElevation(
    geometry: GeoJSON.MultiLineString,
    elevation?: number
): Promise<GeoJSON.MultiLineString>;

async function fetchElevation(
    geometry: GeoJSON.Geometry,
    elevation?: number
): Promise<GeoJSON.Geometry> {
    const missingElevation = hasMissingElevation(geometry);

    if (!missingElevation) {
        return geometry;
    } else if (geometry.type === 'Point' && elevation) {
        return {
            type: 'Point',
            coordinates: [geometry.coordinates[0], geometry.coordinates[1], elevation],
        };
    } else {
        if (
            (geometry.type === 'LineString' || geometry.type === 'MultiLineString') &&
            numberOfCoordinates(geometry) > MAX_VERTICES
        )
            return await fetchLongTrackElevation(geometry);

        try {
            const response = await fetch('https://elevation.gaiagps.com/geojson/', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(geometry),
            });
            return response.json();
        } catch (e) {
            console.error(e);
            return addNoData(geometry);
        }
    }
}

export default memoize<typeof fetchElevation>(fetchElevation, (geometry, elevation) =>
    JSON.stringify([geometry, elevation])
);

// Break (Multi)LineStrings into chunks of MAX_VERTICES and fetch elevation. This prevents us from
// sending geometries with more vertices than MAX_VERTICES to the elevation server.
async function fetchLongTrackElevation(
    geometry: GeoJSON.LineString | GeoJSON.MultiLineString
): Promise<GeoJSON.LineString | GeoJSON.MultiLineString> {
    if (geometry.type === 'LineString') {
        const chunks = chunk(geometry.coordinates, MAX_VERTICES);

        // Very long tracks will have many chunks. We resolve these synchronously to avoid
        // overloading the elevation server with too many requests
        let coordinates: number[][] = [];
        for (let i = 0; i < chunks.length; i++) {
            const elevated = await fetchElevation({
                type: 'LineString',
                coordinates: chunks[i],
            });
            coordinates = coordinates.concat(elevated.coordinates);
        }

        return {type: 'LineString', coordinates};
    } else {
        let coordinates: number[][][] = [];

        for (let i = 0; i < geometry.coordinates.length; i++) {
            const segmentChunks = chunk(geometry.coordinates[i], MAX_VERTICES);

            let segmentCoordinates: number[][] = [];

            for (let j = 0; j < segmentChunks.length; j++) {
                const elevated = await fetchElevation({
                    type: 'LineString',
                    coordinates: segmentChunks[i],
                });
                segmentCoordinates = segmentCoordinates.concat(elevated.coordinates);
            }
            coordinates = coordinates.concat(segmentCoordinates);
        }
        return {type: 'MultiLineString', coordinates};
    }
}

function numberOfCoordinates(geometry: GeoJSON.LineString | GeoJSON.MultiLineString) {
    if (geometry.type === 'LineString') return geometry.coordinates.length;

    return geometry.coordinates.map((segment) => segment.length).reduce((a, b) => a + b, 0);
}

function addNoData(geometry: GeoJSON.Geometry): GeoJSON.Geometry {
    if (geometry.type === 'Point') {
        return {
            type: 'Point',
            coordinates: [...geometry.coordinates.slice(0, 2), NO_DATA],
        };
    } else if (geometry.type === 'LineString') {
        return {
            type: 'LineString',
            coordinates: geometry.coordinates.map((coord) => [...coord.slice(0, 2), NO_DATA]),
        };
    } else if (geometry.type === 'MultiLineString') {
        return {
            type: 'MultiLineString',
            coordinates: geometry.coordinates.map((segment) =>
                segment.map((coord) => [...coord.slice(0, 2), NO_DATA])
            ),
        };
    }
    return geometry;
}

function isInvalidZ(z: number | undefined) {
    return z === undefined || z === NO_DATA;
}

export function hasMissingElevation(geometry: GeoJSON.Geometry): boolean {
    if (geometry.type === 'Point') {
        return isInvalidZ(geometry.coordinates[2]);
    } else if (geometry.type === 'LineString') {
        return geometry.coordinates.some((coord) => isInvalidZ(coord[2]));
    } else if (geometry.type === 'MultiLineString') {
        return geometry.coordinates.some((segment) =>
            segment.some((coord) => isInvalidZ(coord[2]))
        );
    }
    return true;
}
